import { useMemo, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../state/AuthContext";
import { LOCAL_SEED } from "../data/localSeed";
import { PageHeader } from "../components/RequestRouting";

export function PayrollPage() {
  const { moduleIds, homePath, user } = useAuth();
  const [params] = useSearchParams();
  const [w4Ticket, setW4Ticket] = useState<string | null>(null);

  const salaries = LOCAL_SEED.compensation ?? [];
  const bonuses = LOCAL_SEED.bonuses ?? [];
  const w4 = LOCAL_SEED.w4?.[0];
  const current = salaries[0];
  const year = new Date().getFullYear();

  const cyTotal = useMemo(() => {
    const salaryYtd = Number(current?.annual || 0);
    const bonusYtd = bonuses
      .filter((b) => String(b.date).startsWith(String(year)))
      .reduce((s, b) => s + Number(b.amount || 0), 0);
    return salaryYtd + bonusYtd;
  }, [bonuses, current, year]);

  const chart = useMemo(() => {
    const months = Array.from({ length: 12 }, (_, i) => i + 1);
    const monthlySalary = Number(current?.annual || 0) / 12;
    return months.map((m) => {
      const key = `${year}-${String(m).padStart(2, "0")}`;
      const bonus = bonuses
        .filter((b) => String(b.date).startsWith(key))
        .reduce((s, b) => s + Number(b.amount || 0), 0);
      return { m, total: monthlySalary + bonus, label: key.slice(5) };
    });
  }, [bonuses, current, year]);

  const maxBar = Math.max(...chart.map((c) => c.total), 1);

  if (!moduleIds.includes("payroll")) return <Navigate to={homePath} replace />;

  function requestW4() {
    const id = `hd-w4-${Date.now()}`;
    setW4Ticket(id);
  }

  const emphasizeW4 = params.get("action") === "w4";

  return (
    <div className="space-y-6">
      <PageHeader
        title="Payroll"
        subtitle="Your compensation, bonuses, and tax withholding on file."
      />

      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-white border border-black/5 p-4">
          <div className="text-xs uppercase tracking-wider text-slate">Current salary</div>
          <div className="text-2xl font-semibold text-navy mt-1">
            {Number(current?.annual || 0).toLocaleString(undefined, {
              style: "currency",
              currency: "USD",
              maximumFractionDigits: 0,
            })}
          </div>
          <div className="text-sm text-slate mt-1">Effective {String(current?.effective || "")}</div>
        </div>
        <div className="bg-white border border-black/5 p-4 md:col-span-2">
          <div className="text-xs uppercase tracking-wider text-slate mb-2">
            Total compensation {year}
          </div>
          <div className="flex items-end gap-1 h-28">
            {chart.map((c) => (
              <div key={c.m} className="flex-1 flex flex-col items-center gap-1">
                <div
                  className="w-full bg-teal/80 min-h-[2px]"
                  style={{ height: `${(c.total / maxBar) * 100}%` }}
                  title={c.total.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 })}
                />
                <span className="text-[10px] text-slate">{c.label}</span>
              </div>
            ))}
          </div>
          <div className="text-sm text-navy mt-2">
            YTD total{" "}
            {cyTotal.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 })}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-black/5 overflow-hidden">
          <div className="px-4 py-3 border-b border-black/5 font-semibold text-navy">Salary history</div>
          <table className="w-full text-sm">
            <thead className="bg-mist">
              <tr>
                <th className="text-left px-3 py-2 text-slate">Effective</th>
                <th className="text-left px-3 py-2 text-slate">Annual</th>
              </tr>
            </thead>
            <tbody>
              {salaries.map((s) => (
                <tr key={String(s.id)} className="border-t border-black/5">
                  <td className="px-3 py-2">{String(s.effective)}</td>
                  <td className="px-3 py-2">
                    {Number(s.annual).toLocaleString(undefined, {
                      style: "currency",
                      currency: "USD",
                      maximumFractionDigits: 0,
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bg-white border border-black/5 overflow-hidden">
          <div className="px-4 py-3 border-b border-black/5 font-semibold text-navy">Approved bonuses</div>
          <table className="w-full text-sm">
            <thead className="bg-mist">
              <tr>
                <th className="text-left px-3 py-2 text-slate">Date</th>
                <th className="text-left px-3 py-2 text-slate">Type</th>
                <th className="text-left px-3 py-2 text-slate">Amount</th>
              </tr>
            </thead>
            <tbody>
              {bonuses.map((b) => (
                <tr key={String(b.id)} className="border-t border-black/5">
                  <td className="px-3 py-2">{String(b.date)}</td>
                  <td className="px-3 py-2">{String(b.type)}</td>
                  <td className="px-3 py-2">
                    {Number(b.amount).toLocaleString(undefined, {
                      style: "currency",
                      currency: "USD",
                      maximumFractionDigits: 0,
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-black/5 p-4 space-y-2">
          <h2 className="font-semibold text-navy">Direct deposit</h2>
          {(LOCAL_SEED.direct_deposit ?? []).map((d) => (
            <div key={String(d.id)} className="text-sm border border-black/5 p-3">
              <div className="font-medium text-navy">{String(d.bank)}</div>
              <div className="text-slate">
                {String(d.account_type)} · routing {String(d.routing_masked)} · account{" "}
                {String(d.account_masked)}
              </div>
              <div className="text-xs text-teal mt-1">{String(d.status)}</div>
            </div>
          ))}
        </div>
        <div className="bg-white border border-black/5 overflow-hidden">
          <div className="px-4 py-3 border-b border-black/5 font-semibold text-navy">Pay stubs</div>
          <table className="w-full text-sm">
            <thead className="bg-mist">
              <tr>
                <th className="text-left px-3 py-2 text-slate">Period</th>
                <th className="text-left px-3 py-2 text-slate">Pay date</th>
                <th className="text-left px-3 py-2 text-slate">Net</th>
                <th className="text-left px-3 py-2 text-slate">PDF</th>
              </tr>
            </thead>
            <tbody>
              {(LOCAL_SEED.pay_stubs ?? []).map((p) => (
                <tr key={String(p.id)} className="border-t border-black/5">
                  <td className="px-3 py-2">{String(p.period)}</td>
                  <td className="px-3 py-2">{String(p.pay_date)}</td>
                  <td className="px-3 py-2">
                    {Number(p.net).toLocaleString(undefined, { style: "currency", currency: "USD" })}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      className="text-teal hover:underline"
                      title="Demo PDF reference"
                      onClick={() =>
                        alert(`Demo: would download ${String(p.pdf)}`)
                      }
                    >
                      Download
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div
        className={`bg-white border p-4 space-y-3 ${
          emphasizeW4 ? "border-teal ring-2 ring-teal/30" : "border-black/5"
        }`}
      >
        <h2 className="font-semibold text-navy">Latest W-4 on file</h2>
        {w4 ? (
          <dl className="grid sm:grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-xs uppercase text-slate">Filing status</dt>
              <dd className="text-navy">{String(w4.filing_status)}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-slate">Effective</dt>
              <dd className="text-navy">{String(w4.effective)}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-slate">Allowances / dependents</dt>
              <dd className="text-navy">{String(w4.dependents)}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-slate">Extra withholding</dt>
              <dd className="text-navy">{String(w4.extra_withholding)}</dd>
            </div>
          </dl>
        ) : (
          <p className="text-sm text-slate">No W-4 on file.</p>
        )}
        <button type="button" className="px-4 py-2 bg-navy text-white text-sm" onClick={requestW4}>
          Update W-4 (opens HR help desk ticket)
        </button>
        {w4Ticket && (
          <p className="text-sm text-teal">
            Ticket {w4Ticket} drafted for HR on behalf of {user?.email ?? "you"} — open Help desk to track it, or
            submit a ticket with subject “W-4 update”.
          </p>
        )}
      </div>
    </div>
  );
}
